<template>
  <div class="modal-overlay" @click.self="emit('close')">
    <div class="modal-container">
      <div class="modal-header">
        <button class="modal-close-button" @click="emit('close')" aria-label="Close verification modal">
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M18 6L6 18M6 6L18 18" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
          </svg>
        </button>
        <div class="header-content">
          <div class="icon-wrapper">
            <span class="lock-icon">🔒</span>
          </div>
          <div>
            <p class="eyebrow">Security Check</p>
            <h3>Verify your email</h3>
          </div>
        </div>
      </div>

      <div class="modal-body">
        <p class="lead">
          We've sent a 6-digit code to <span class="highlight">{{ email || username || 'your email' }}</span>.
          <br>Please enter it below to verify your account.
        </p>

        <div class="code-input-wrapper">
          <input
            id="verification-code"
            ref="codeInput"
            type="text"
            inputmode="numeric"
            autocomplete="one-time-code"
            maxlength="6"
            placeholder="000000"
            :value="code"
            @input="handleInput"
            :disabled="isSubmitting"
            class="code-input"
            :class="{ 'has-error': error }"
          />
          <div class="focus-border"></div>
        </div>

        <div v-if="error" class="error-message">
          <span class="error-icon">⚠️</span>
          {{ error }}
        </div>

        <div class="actions">
          <button 
            class="secondary-btn" 
            @click="emit('resend')" 
            :disabled="isSubmitting || isResending"
          >
            <span v-if="isResending" class="spinner-sm"></span>
            <span v-else>Resend Code</span>
          </button>
          
          <button 
            class="primary-btn" 
            @click="emit('confirm')" 
            :disabled="isSubmitting || code.length < 6"
          >
            <span v-if="isSubmitting" class="spinner-sm"></span>
            <span v-else>Verify & Continue</span>
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, onMounted } from 'vue'

const props = defineProps<{
  email?: string
  username?: string
  code: string
  isSubmitting: boolean
  isResending: boolean
  error?: string
}>()

const emit = defineEmits<{
  (e: 'close'): void
  (e: 'resend'): void
  (e: 'confirm'): void
  (e: 'update:code', value: string): void
}>()

const codeInput = ref<HTMLInputElement | null>(null)

const handleInput = (event: Event) => {
  const target = event.target as HTMLInputElement
  const value = target.value.replace(/\D/g, '').slice(0, 6)
  emit('update:code', value)
}

onMounted(() => {
  if (codeInput.value) {
    codeInput.value.focus()
  }
})
</script>

<style scoped>
.modal-overlay {
  animation: fadeIn 0.3s ease-out;
}

.header-content {
  display: flex;
  align-items: center;
  gap: 16px;
  width: 100%;
}

.icon-wrapper {
  width: 40px;
  height: 40px;
  border-radius: 12px;
  background: rgba(16, 26, 52, 0.12);
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 20px;
}

.eyebrow {
  margin: 0 0 2px 0;
  text-transform: uppercase;
  letter-spacing: 0.1em;
  font-size: 0.7rem;
  color: var(--ui-inverse-text);
  font-weight: 600;
  font-family: var(--ui-inverse-font-family);
}

.modal-body {
  display: flex;
  flex-direction: column;
  gap: 24px;
}

.lead {
  margin: 0;
  line-height: 1.6;
  color: var(--ui-inverse-text-muted);
  text-align: center;
  font-size: 1rem;
  font-family: var(--ui-inverse-font-family);
}

.highlight {
  color: var(--ui-inverse-text);
  font-weight: 600;
}

.code-input-wrapper {
  position: relative;
  width: 100%;
  display: flex;
  justify-content: center;
}

.code-input {
  width: 100%;
  padding: 16px;
  border-radius: 999px;
  border: 1px solid var(--ui-inverse-control-border);
  background: var(--ui-inverse-control-bg);
  color: var(--ui-inverse-control-text);
  font-size: 2rem;
  font-weight: 700;
  letter-spacing: 0.5em;
  text-align: center;
  transition: all 0.3s ease;
  font-family: var(--ui-inverse-font-family);
  box-shadow: inset 0 2px 4px rgba(0, 0, 0, 0.14);
}

.code-input:focus {
  outline: none;
  border-color: rgba(16, 26, 52, 0.42);
  box-shadow: 
    inset 0 2px 4px rgba(0, 0, 0, 0.14),
    0 0 0 4px rgba(16, 26, 52, 0.08);
  background: var(--ui-inverse-control-bg-hover);
}

.code-input.has-error {
  border-color: #8f1434;
  color: #8f1434;
}

.code-input::placeholder {
  color: var(--ui-inverse-control-placeholder);
  letter-spacing: 0.5em;
}

.error-message {
  display: flex;
  align-items: center;
  gap: 8px;
  background: rgba(143, 20, 52, 0.08);
  border: 1px solid rgba(143, 20, 52, 0.2);
  color: #8f1434;
  padding: 12px 16px;
  border-radius: 12px;
  font-size: 0.9rem;
  justify-content: center;
  animation: shake 0.5s cubic-bezier(.36,.07,.19,.97) both;
}

.actions {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 16px;
  margin-top: 8px;
}

.primary-btn, .secondary-btn {
  padding: 14px 20px;
  border-radius: 12px;
  font-weight: 600;
  font-size: 1rem;
  cursor: pointer;
  transition: all 0.2s;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  font-family: var(--ui-inverse-font-family);
}

.primary-btn {
  background: linear-gradient(135deg, var(--vt-c-accent) 0%, #56dca0 100%);
  border: none;
  color: #0f121e;
  box-shadow: 0 4px 12px rgba(var(--vt-c-accent-rgb), 0.25);
}

.primary-btn:hover:not(:disabled) {
  transform: none;
  filter: none;
}

.primary-btn:disabled {
  opacity: 0.6;
  cursor: not-allowed;
  background: #4b5563;
  color: #9ca3af;
  box-shadow: none;
}

.secondary-btn {
  background: transparent;
  border: 1px solid var(--ui-inverse-border-strong);
  color: var(--ui-inverse-text);
}

.secondary-btn:hover:not(:disabled) {
  background: rgba(16, 26, 52, 0.08);
  border-color: rgba(16, 26, 52, 0.36);
  color: var(--ui-inverse-text);
}

.secondary-btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.spinner-sm {
  width: 16px;
  height: 16px;
  border: 2px solid transparent;
  border-top-color: currentColor;
  border-radius: 50%;
  animation: spin 0.8s linear infinite;
}

@keyframes spin {
  to { transform: rotate(360deg); }
}

@keyframes fadeIn {
  from { opacity: 0; }
  to { opacity: 1; }
}

@keyframes shake {
  10%, 90% { transform: translate3d(-1px, 0, 0); }
  20%, 80% { transform: translate3d(2px, 0, 0); }
  30%, 50%, 70% { transform: translate3d(-4px, 0, 0); }
  40%, 60% { transform: translate3d(4px, 0, 0); }
}
</style>
